import React, { useEffect, useState, useCallback } from 'react';
import websocketApi from 'api/websocket';
import actions from 'actions';
import { setupNotifications, supportsPush } from 'utils/pushMessaging';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import styled from 'styled-components';
import { ToastContainer, ToastIcon } from 'components/lib/Toast';
import PushSetupModal from 'components/lib/PushSetupModal';
import { Modal, Button } from 'semantic-ui-react';
import { useReward } from 'react-rewards';
import { useAppSelector } from 'store';
import { ThemedButton } from './lib/UI';
import util from 'utils/utils';

const StyledAchievementModal = styled.div`
  width: 450px;
  max-width: 90%;
  margin: 0 auto;
  > div {
    color: black;
    font-size: 1.3em;
    border-radius: 7.5px;
    padding: 20px;
    background-color: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
    .confetti-spawner {
      width: 1px;
      height: 1px;
      display: block;
    }
    .congratulations-title {
      font-weight: bold;
      color: gray;
      margin-bottom: 15px;
    }
    .achievement-icon{
      line-height: 80px;
      font-size: 80px;
      margin: 20px 0;
      position:relative;
    }
    .achievement-name {
      font-size: 1.3em;
      font-weight: bold;
      display: block;
      margin-bottom: 5px;
    }
    .achievement-description {
      font-size: 0.9em;
      color: #a5a5a5;
      display: block;
      margin-bottom: 5px;
    }
    .achievement-actions {
      display: flex;
      margin-top: 30px;
      width: 100%;
      justify-content: flex-end;
      .ui.button {
        margin-left: 5px;
      }
    }
  }
`;

const EventSubscriptions = ({
  incrementNewMessageCount,
}) => {
  const dispatch = useDispatch();

  const user = useAppSelector((state) => state.user);
  const messageThreads = useAppSelector((state) => state.messages.messageThreads);
  const setTrayOpen = useCallback((open) => dispatch(actions.notifications.setTrayOpen(open)), [dispatch]);
  const onMessageThreadSelected = useCallback((thread) => dispatch(actions.messages.selectThread(thread)), [dispatch]);
  const userId = user?._id;

  const [activeAchievement, setActiveAchievement] = useState({});
  const [achievementModalOpen, setAchievementModalOpen] = useState(false);
  const [pushSetupOpen, setPushSetupOpen] = useState(false);

  const { reward } = useReward('achievementAwarded', 'confetti', { lifetime: 200 });

  useEffect(() => {
    const achievementSubscription = websocketApi.subscribe(`unlockedAchievement-${userId}`, (achievement) => {
      if (achievement.key) {
        setActiveAchievement(achievement);
        setAchievementModalOpen(true);
        reward();
      }
    });
    return () => achievementSubscription.unsubscribe();
  // Including reward() in the dependencies currently causes this effect to run on every page change
  // Awaiting an update to react-rewards to change this; Currently it doesn't matter to include it for us as we only ever use a single configuration for the reward
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    const messageSubscription = websocketApi.subscribe(`newMessage-${userId}`, (message) => {
      if (message.message && message.thread) {
        const thread = messageThreads.find((t) => t._id === message.thread);
        toast((t) => (
          <ToastContainer
            onClick={() => {
              if (thread) onMessageThreadSelected(thread);
              toast.dismiss(t.id);
            }}
          >
            <div className='icon-container'>
              <ToastIcon name='envelope' />
            </div>
            <div className='info-container'>
              <span><b>{message.user.profile.firstName}</b> sent you a message</span>
              <span className='meta'>"{message.message}"</span>
            </div>
          </ToastContainer>
        ));
      }
    });
    return () => messageSubscription.unsubscribe();
  }, [messageThreads, onMessageThreadSelected, incrementNewMessageCount, userId]);

  const pushHandler = useCallback((notification) => {
    if (userId && supportsPush()) {
      if (Notification.permission === 'granted') {
        setupNotifications();
      } else if (Notification.permission === 'default') {
        if (util.localStorageIsSupported()) {
          const hasSeenPushSetupModal = localStorage.getItem('hasSeenPushSetupModal');
          if (!hasSeenPushSetupModal) {
            setPushSetupOpen(true);
            localStorage.setItem('hasSeenPushSetupModal', true);
          }
        } else {
          setPushSetupOpen(true);
        }
      }
    }
    // We may manually choose to surpress some notifications; For example, direct messages are already shown as toasts, therefore we hide the double toast
    if (notification.title && !notification.extra?.suppressInAppNotification) {
      toast((t) => (
        <ToastContainer
          onClick={() => {
            setTrayOpen(true);
            toast.dismiss(t.id);
          }}
        >
          <div className='icon-container'>
            <ToastIcon name='bell' />
          </div>
          <div className='info-container'>
            <span>{notification.title}</span>
            <span className='meta'>{notification.message}</span>
          </div>
        </ToastContainer>
      ));
    }
  }, [setTrayOpen, userId]);

  useEffect(() => {
    const notificationSubscription = websocketApi.subscribe(`pushNotification-${userId}`, pushHandler);
    return notificationSubscription.unsubscribe;
  }, [pushHandler, userId]);

  return (
    <div>
      <PushSetupModal open={pushSetupOpen} setOpen={setPushSetupOpen} />
      <Modal
        basic
        open={achievementModalOpen}
        onClose={() => setAchievementModalOpen(false)}
      >
        <StyledAchievementModal>
          <div>
            <span id='achievementAwarded' />
            <span className='congratulations-title'>
              Congratulations, you unlocked an achievement!
            </span>
            <span role='img' aria-label={activeAchievement.name} className='achievement-icon' dangerouslySetInnerHTML={{ __html: activeAchievement.icon }} />
            <span className='achievement-name'>
              {activeAchievement.name}
            </span>
            <span className='achievement-description'>
              {activeAchievement.description}
            </span>
            <div className='achievement-actions'>
              <Button
                size='tiny'
                content='View all achievements'
                as={Link}
                to='/achievements'
                onClick={() => setAchievementModalOpen(false)}
              />
              <ThemedButton
                size='tiny'
                primary
                content='Done'
                icon='check'
                onClick={() => setAchievementModalOpen(false)}
              />
            </div>
          </div>
        </StyledAchievementModal>
      </Modal>
    </div>
  );
};

export default EventSubscriptions;
